import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { HealthGauge } from "@/components/HealthGauge";
import { api, fileUrl, fetchImageEl, apiError } from "@/lib/api";
import { Sparkles, Lightbulb, Heart, ArrowLeftRight, RotateCw, Download, Share2, Target, Check } from "lucide-react";

export default function Reveal() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [phase, setPhase] = useState("checking"); // checking, processing, done, failed
  const [gen, setGen] = useState(null);
  const pollRef = useRef(0);

  const pollGeneration = useCallback(async () => {
    try {
      const { data } = await api.get(`/generations/${id}`);
      if (data.status === "complete") {
        setGen(data);
        setPhase("done");
        return;
      }
      if (data.status === "failed") {
        setPhase("failed");
        return;
      }
      if (data.status === "awaiting_payment") {
        setPhase("failed");
        return;
      }
      setPhase("processing");
      pollRef.current += 1;
      if (pollRef.current < 40) setTimeout(pollGeneration, 3000);
      else setPhase("failed");
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [id]);

  const pollPayment = useCallback(async (attempt = 0) => {
    if (!sessionId) {
      // maybe already paid (revisiting from history)
      pollGeneration();
      return;
    }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPhase("processing");
        pollGeneration();
        return;
      }
      if (data.status === "expired" || attempt >= 8) {
        setPhase("failed");
        toast.error("Payment not completed.");
        return;
      }
      setTimeout(() => pollPayment(attempt + 1), 2000);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [sessionId, pollGeneration]);

  useEffect(() => {
    pollPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 pt-10 pb-24">
        {phase !== "done" && phase !== "failed" && <Analyzing phase={phase} />}
        {phase === "failed" && (
          <div className="text-center pt-20">
            <h2 className="font-heading text-3xl text-charcoal mb-3">Something went wrong</h2>
            <p className="text-muted-c mb-6">We couldn't complete your reveal. Please try again.</p>
            <Link to="/create" className="btn-primary inline-block px-8 py-3.5 font-semibold">Try again</Link>
          </div>
        )}
        {phase === "done" && gen && <Result gen={gen} />}
      </div>
    </div>
  );
}

const Analyzing = ({ phase }) => (
  <div className="flex flex-col items-center justify-center pt-24 text-center">
    <div className="relative mb-8">
      <Sparkles className="w-14 h-14 spin-slow" style={{ color: "var(--primary)" }} />
    </div>
    <h2 className="font-heading text-3xl text-charcoal mb-3">
      {phase === "checking" ? "Confirming your payment…" : "Ageing your future self…"}
    </h2>
    <p className="text-muted-c max-w-sm">
      Our AI is painting your future self at +10, +20 and +30 years, plus your healthiest possible
      path. This takes up to a minute.
    </p>
  </div>
);

function Result({ gen }) {
  const [pos, setPos] = useState(50);
  const [ageVal, setAgeVal] = useState(20);
  const [sharing, setSharing] = useState(false);
  const [committed, setCommitted] = useState([]);
  const [captionStyle, setCaptionStyle] = useState("funny");
  const agePaths = gen.aged_paths && Object.keys(gen.aged_paths).length ? gen.aged_paths : { "20": gen.aged_path };
  const availableAges = ["10", "20", "30"].filter((a) => agePaths[a]);
  const multiAge = availableAges.length > 1;
  const before = fileUrl(gen.selfie_path);

  // nearest discrete age for card export
  const nearestAge = availableAges.reduce(
    (best, a) => (Math.abs(Number(a) - ageVal) < Math.abs(Number(best) - ageVal) ? a : best),
    availableAges[0] || "20"
  );

  // crossfade opacities across the +10/+20/+30 anchors -> smooth morph
  const layerOpacity = (a) => {
    if (!multiAge) return 1;
    const o = 1 - Math.abs(Number(a) - ageVal) / 10;
    return Math.max(0, Math.min(1, o));
  };

  useEffect(() => {
    api.get("/goals").then((r) => {
      const active = r.data.filter((g) => g.status === "active").map((g) => g.tip);
      setCommitted((c) => Array.from(new Set([...c, ...active])));
    }).catch(() => {});
  }, []);

  const commit = async (tip) => {
    try {
      await api.post("/goals", { generation_id: gen.id, tip });
      setCommitted((c) => [...c, tip]);
      toast.success("Goal added! We'll nudge you to check back in a week. 💪");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const buildCard = async () => {
    const beforeImg = await fetchImageEl(gen.selfie_path);
    const afterImg = await fetchImageEl(agePaths[nearestAge] || gen.aged_path);
    const W = 1200, H = 760, pad = 60, imgW = (W - pad * 3) / 2, imgH = imgW * 1.25;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FAF8F5";
    ctx.fillRect(0, 0, W, H);

    const drawCover = (img, x, y, w, h, r) => {
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.clip();
      const ar = img.width / img.height, tr = w / h;
      let sw = img.width, sh = img.height, sx = 0, sy = 0;
      if (ar > tr) { sw = img.height * tr; sx = (img.width - sw) / 2; }
      else { sh = img.width / tr; sy = (img.height - sh) / 2; }
      ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
      ctx.restore();
    };

    const top = 150;
    drawCover(beforeImg, pad, top, imgW, imgH, 28);
    drawCover(afterImg, pad * 2 + imgW, top, imgW, imgH, 28);

    ctx.fillStyle = "#1A1A1A";
    ctx.font = "600 30px 'Playfair Display', serif";
    ctx.fillText("20 Year Self — My Future Self", pad, 80);
    ctx.fillStyle = "#8E9E82";
    ctx.font = "700 16px Manrope, sans-serif";
    ctx.fillText(`HEALTH SCORE  ${gen.health_score}/100`, pad, 115);

    const label = (text, x, bg) => {
      ctx.font = "700 18px Manrope, sans-serif";
      const w = ctx.measureText(text).width + 32;
      ctx.fillStyle = bg;
      ctx.beginPath();
      ctx.roundRect(x, top + 20, w, 40, 20);
      ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.fillText(text, x + 16, top + 46);
    };
    label("NOW", pad + 20, "rgba(26,26,26,0.75)");
    label(`+${nearestAge} YEARS`, pad * 2 + imgW + 20, "#E88D67");

    ctx.fillStyle = "#666";
    ctx.font = "500 20px Manrope, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Discover your future self at auraglow", W / 2, H - 40);
    ctx.textAlign = "left";

    return await new Promise((res) => canvas.toBlob(res, "image/png"));
  };

  const share = async () => {
    setSharing(true);
    try {
      let caption = "";
      try {
        const { data } = await api.post(`/generations/${gen.id}/caption?style=${captionStyle}`);
        caption = data.caption || "";
      } catch (_) {}

      const blob = await buildCard();
      const file = new File([blob], "auraglow-future-self.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My 20 Year Self", text: caption });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "auraglow-future-self.png";
        a.click();
        URL.revokeObjectURL(url);
        if (caption) {
          try {
            await navigator.clipboard.writeText(caption);
            toast.success("Card saved & caption copied to clipboard! 📋");
          } catch (_) {
            toast.success("Card downloaded!");
          }
        } else {
          toast.success("Card downloaded!");
        }
      }
    } catch (e) {
      toast.error("Couldn't create the card. Please try again.");
    } finally {
      setSharing(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
      <div className="text-center mb-10">
        <span className="text-xs tracking-[0.15em] uppercase font-bold text-sage">Your future self reveal</span>
        <h1 className="font-heading text-4xl sm:text-5xl text-charcoal mt-2">This is future you.</h1>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-7">
          <div className="relative rounded-3xl overflow-hidden soft-shadow border border-black/5 aspect-[4/5] select-none" data-testid="before-after">
            {availableAges.map((a) => (
              <img
                key={a}
                src={fileUrl(agePaths[a])}
                alt={`future +${a}`}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ opacity: layerOpacity(a), transition: "opacity 0.15s linear" }}
              />
            ))}
            <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
              <img src={before} alt="now" className="absolute inset-0 h-full object-cover" style={{ width: "100%", maxWidth: "none" }} />
            </div>
            <div className="absolute top-0 bottom-0" style={{ left: `${pos}%`, width: 2, background: "#fff", transform: "translateX(-1px)" }} />
            <div className="absolute top-4 left-4 glass px-3 py-1 rounded-full text-xs font-bold text-charcoal">NOW</div>
            <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold text-white" style={{ background: "var(--primary)" }}>+{Math.round(ageVal)} YRS</div>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 glass px-3 py-1.5 rounded-full text-xs text-charcoal">
              <ArrowLeftRight className="w-3.5 h-3.5" /> Drag to compare
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={pos}
              data-testid="reveal-slider"
              onChange={(e) => setPos(Number(e.target.value))}
              className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
            />
          </div>

          {multiAge && (
            <div className="mt-5 bg-white rounded-3xl p-5 soft-shadow border border-black/5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs tracking-[0.15em] uppercase font-bold text-sage">Age morph</span>
                <span className="font-heading text-lg text-charcoal">+{Math.round(ageVal)} years</span>
              </div>
              <input
                type="range"
                min="10"
                max="30"
                step="1"
                value={ageVal}
                data-testid="age-slider"
                onChange={(e) => setAgeVal(Number(e.target.value))}
                className="w-full accent-[var(--primary)] cursor-ew-resize"
                style={{ accentColor: "var(--primary)" }}
              />
              <div className="flex justify-between mt-1 text-xs text-muted-c">
                <span>+10</span><span>+20</span><span>+30</span>
              </div>
              <p className="text-xs text-muted-c mt-2">Drag to watch your face age in real time.</p>
            </div>
          )}

          <div className="mt-5">
            <div className="text-xs tracking-[0.15em] uppercase font-bold text-sage mb-2">Caption vibe</div>
            <div className="flex gap-2" data-testid="caption-styles">
              {[
                { id: "funny", label: "Funny" },
                { id: "inspiring", label: "Inspiring" },
                { id: "savage", label: "Savage" },
              ].map((s) => {
                const active = captionStyle === s.id;
                return (
                  <button
                    key={s.id}
                    data-testid={`caption-style-${s.id}`}
                    onClick={() => setCaptionStyle(s.id)}
                    className="flex-1 py-2.5 rounded-full border-2 font-semibold text-sm transition-colors"
                    style={{
                      borderColor: active ? "var(--primary)" : "rgba(26,26,26,0.08)",
                      background: active ? "rgba(232,141,103,0.08)" : "#fff",
                      color: "var(--charcoal)",
                    }}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex gap-3 mt-4">
            <button data-testid="share-btn" onClick={share} disabled={sharing} className="btn-primary flex-1 py-3.5 font-semibold flex items-center justify-center gap-2">
              <Share2 className="w-4 h-4" /> {sharing ? "Creating…" : "Share card"}
            </button>
            <button data-testid="download-btn" onClick={share} disabled={sharing} className="py-3.5 px-5 font-semibold rounded-full border border-black/10 hover:bg-black/5 transition-colors flex items-center justify-center gap-2">
              <Download className="w-4 h-4" /> Save
            </button>
          </div>
        </div>

        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white rounded-3xl p-8 soft-shadow border border-black/5 flex flex-col items-center">
            <HealthGauge score={gen.health_score || 0} />
            <p className="text-sm text-muted-c mt-4 text-center">
              {gen.health_score >= 75
                ? "Amazing! Your habits are ageing you beautifully."
                : gen.health_score >= 50
                ? "Solid foundation — a few tweaks unlock your glow."
                : "Big glow-up potential — small changes go a long way."}
            </p>
          </div>

          {gen.strengths?.length > 0 && (
            <div className="bg-white rounded-3xl p-6 soft-shadow border border-black/5">
              <div className="flex items-center gap-2 mb-3">
                <Heart className="w-4 h-4" style={{ color: "var(--sage)" }} />
                <span className="text-xs tracking-[0.15em] uppercase font-bold text-sage">What you're nailing</span>
              </div>
              <ul className="space-y-2">
                {gen.strengths.map((s, i) => (
                  <li key={i} className="text-sm text-charcoal leading-relaxed">{s}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {gen.healthy_path && (
        <div className="mt-14" data-testid="healthy-future">
          <div className="text-center mb-6">
            <span className="text-xs tracking-[0.15em] uppercase font-bold text-sage">The choice is yours</span>
            <h2 className="font-heading text-3xl text-charcoal mt-2">Two paths, 20 years from now</h2>
            <p className="text-sm text-muted-c mt-2 max-w-lg mx-auto">
              Same you, same 20 years — but healthier habits rewrite the ending. This could be earned.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
            <div>
              <div className="rounded-3xl overflow-hidden border border-black/5 soft-shadow aspect-[4/5] relative">
                <img src={fileUrl(agePaths["20"] || gen.aged_path)} alt="current path" className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3 glass px-3 py-1 rounded-full text-xs font-bold text-charcoal">CURRENT PATH</div>
              </div>
            </div>
            <div>
              <div className="rounded-3xl overflow-hidden border-2 soft-shadow aspect-[4/5] relative" style={{ borderColor: "var(--sage)" }}>
                <img src={fileUrl(gen.healthy_path)} alt="healthy path" className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold text-white" style={{ background: "var(--sage)" }}>HEALTHY PATH</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {gen.tips?.length > 0 && (
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-5 h-5" style={{ color: "var(--primary)" }} />
            <h2 className="font-heading text-2xl text-charcoal">Your glow-up plan</h2>
          </div>
          <p className="text-sm text-muted-c mb-5">Commit to one and we'll nudge you to check back in a week.</p>
          <div className="grid md:grid-cols-2 gap-4" data-testid="tips-list">
            {gen.tips.map((t, i) => {
              const done = committed.includes(t);
              return (
                <motion.div
                  key={i}
                  className="bg-white rounded-3xl p-6 soft-shadow border border-black/5 flex flex-col"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                >
                  <div className="font-heading text-lg mb-1" style={{ color: "var(--primary)" }}>{String(i + 1).padStart(2, "0")}</div>
                  <p className="text-sm text-charcoal leading-relaxed flex-1">{t}</p>
                  <button
                    data-testid={`commit-tip-${i}`}
                    onClick={() => !done && commit(t)}
                    disabled={done}
                    className="mt-4 self-start inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-2 border transition-colors"
                    style={{
                      borderColor: done ? "var(--sage)" : "rgba(26,26,26,0.12)",
                      color: done ? "var(--sage)" : "var(--charcoal)",
                      background: done ? "rgba(142,158,130,0.1)" : "#fff",
                    }}
                  >
                    {done ? (<><Check className="w-4 h-4" /> Committed</>) : (<><Target className="w-4 h-4" /> Commit to this</>)}
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex justify-center gap-4 mt-12">
        <Link to="/create" data-testid="reveal-again-btn" className="btn-primary px-8 py-3.5 font-semibold inline-flex items-center gap-2">
          <RotateCw className="w-4 h-4" /> New Glow-Up
        </Link>
        <Link to="/history" className="px-8 py-3.5 font-semibold rounded-full border border-black/10 hover:bg-black/5 transition-colors">
          View History
        </Link>
      </div>
    </motion.div>
  );
}
